// Decyzon — source-options coercion helper.
//
// CONTRACT TRUTH RULE: every visible side/outcome label must come from the
// source contract's `options[]` array (Polymarket outcomes / Kalshi
// contracts). This helper turns the loosely-typed value carried on a
// market_feed row or PredictionResult into the strict shape consumed by
// `deriveAction` and `deriveForcedDecision`.
//
// We never synthesize entries. If the input is missing/invalid we return [].

export interface SourceOptionLike {
  label: string;
  market_probability?: number | null;
  estimated_probability?: number | null;
  edge_pct?: number | null;
}

function normalize(label: unknown): string | null {
  if (typeof label !== "string") return null;
  const t = label.replace(/\s+/g, " ").trim();
  return t.length > 0 ? t : null;
}

function num(v: unknown): number | null {
  if (typeof v !== "number" || !Number.isFinite(v)) return null;
  return v;
}

/** Coerce arbitrary `options` (Json from DB or typed ResultOption[]) into the
 *  strict SourceOptionLike[] shape. Drops any entry without a usable label. */
export function coerceSourceOptions(raw: unknown): SourceOptionLike[] {
  if (!Array.isArray(raw)) return [];
  const out: SourceOptionLike[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const o = item as Record<string, unknown>;
    // Tolerate label / name / outcome keys — sources differ.
    const label =
      normalize(o.label) ?? normalize(o.name) ?? normalize(o.outcome);
    if (!label) continue;
    out.push({
      label,
      market_probability: num(o.market_probability) ?? num(o.probability) ?? num(o.price),
      estimated_probability: num(o.estimated_probability),
      edge_pct: num(o.edge_pct),
    });
  }
  return out;
}
